// "Don't want to cook? Order it": find the dish near the user on a food app (Swiggy…), review the
// cart with the app's own prices, and place the order only after an explicit confirmation that
// shows the items, total, delivery address and payment method (Swiggy's rules require it).
import { useEffect, useRef, useState } from 'preact/hooks';
import { api, ApiError } from '../api';
import { Link } from '../router';
import { ComingSoon } from '../components/ComingSoon';
import { Back, SearchIcon, VegMark } from '../icons';
import { ErrorBox, useToast } from '../components/Shell';
import { Thumb } from '../components/Thumb';
import { AddressCard, CartSummary, ReviewActions } from '../components/ReviewParts';
import { money, startConnect, useProviders } from '../commerce';
import { useT } from '../i18n';
import { load, save } from '../storage';
import type { Address, Dish, FoodCart, FoodOrder as Order, OrderStatus } from '../types';

type Step = 'address' | 'search' | 'review' | 'done';

export function FoodOrder({ provider }: { provider: string }) {
  const t = useT();
  const params = new URLSearchParams(location.search);
  const [providers] = useProviders();
  const p = providers.find((x) => x.id === provider);
  const name = p?.name ?? provider;
  const addrKey = `er.food.address.${provider}`;

  const [step, setStep] = useState<Step>('address');
  const [addresses, setAddresses] = useState<Address[] | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  const [q, setQ] = useState(params.get('dish') || '');
  const [veg, setVeg] = useState(params.get('veg') === '1');
  const [dishes, setDishes] = useState<Dish[] | null>(null);
  const [dish, setDish] = useState<Dish | null>(null);
  const [qty, setQty] = useState(1);
  const [cart, setCart] = useState<FoodCart | null>(null);
  const [method, setMethod] = useState('');
  const [note, setNote] = useState('');
  const [order, setOrder] = useState<Order | null>(null);
  const [track, setTrack] = useState<OrderStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [toast, showToast] = useToast();
  const placing = useRef(false);

  useEffect(() => {
    if (!p?.connected) return;
    api.addresses(provider).then((as) => {
      setAddresses(as);
      const saved = as.find((a) => a.id === load<string>(addrKey, ''));
      if (saved) { setAddress(saved); setStep('search'); }
    }).catch((e: ApiError) => setErr(e.message));
  }, [p?.connected]);

  const search = async () => {
    if (!address || !q.trim()) return;
    setBusy(true);
    setErr('');
    try { setDishes(await api.foodSearch(provider, address.id, q.trim(), veg)); } catch (e) { setErr((e as ApiError).message); } finally { setBusy(false); }
  };
  useEffect(() => { if (step === 'search' && address && q.trim() && dishes === null) search(); }, [step, address?.id]);

  const review = async () => {
    if (!dish || !address) return;
    setBusy(true);
    setErr('');
    try {
      const c = await api.foodCart(provider, { address_id: address.id, restaurant_id: dish.restaurant_id, restaurant_name: dish.restaurant_name, items: [{ item_id: dish.item_id, quantity: qty }] });
      setCart(c);
      setMethod(c.payments.length === 1 ? c.payments[0].method : '');
      setStep('review');
      window.scrollTo(0, 0);
    } catch (e) { setErr((e as ApiError).message); } finally { setBusy(false); }
  };

  const place = async () => {
    if (!address || !method || placing.current) return; // never double-submit an order
    placing.current = true;
    setBusy(true);
    setErr('');
    try {
      const o = await api.foodOrder(provider, { address_id: address.id, payment_method: method, note: note.trim(), confirmed: true });
      setOrder(o);
      setStep('done');
      window.scrollTo(0, 0);
    } catch (e) {
      setErr((e as ApiError).message);
      placing.current = false;
    } finally { setBusy(false); }
  };

  // UPI: poll the payment until it settles (respecting the provider's interval), for up to 5 minutes.
  useEffect(() => {
    if (order?.status !== 'pending_payment') return;
    const started = Date.now();
    const every = Math.max(order.poll_ms ?? 4000, 3000);
    const id = setInterval(async () => {
      if (Date.now() - started > 5 * 60_000) { clearInterval(id); return; }
      try {
        const next = await api.orderPayment(provider, order.id);
        if (next.status !== 'pending_payment') { setOrder({ ...order, ...next }); clearInterval(id); }
      } catch { /* keep polling; the next tick retries */ }
    }, every);
    return () => clearInterval(id);
  }, [order?.id, order?.status]);

  const refreshTrack = () => order && api.orderTrack(provider, order.id, address?.id ?? '').then(setTrack).catch((e: ApiError) => showToast(e.message));
  useEffect(() => { if (order?.status === 'confirmed') refreshTrack(); }, [order?.status]);

  const methodLabel = cart?.payments.find((x) => x.method === method)?.label ?? '';

  if (p?.coming_soon) return <ComingSoon name={name} back="/" />;
  return (
    <div class="page" style="padding-bottom:calc(130px + env(safe-area-inset-bottom))">
      <div class="backbar">
        {step === 'review'
          ? <button class="icon-btn" aria-label={t('common.back')} onClick={() => setStep('search')}><Back /></button>
          : <button class="icon-btn" aria-label={t('common.back')} onClick={() => history.back()}><Back /></button>}
      </div>
      <div class="pad" style="display:flex;flex-direction:column;gap:4px">
        <h1 class="h1" style="font-size:28px">{step === 'review' ? t('food.reviewTitle') : step === 'done' ? t('food.doneTitle') : t('food.title', { app: name })}</h1>
        {p?.attribution && <span class="small muted">{p.attribution}</span>}
      </div>

      {params.get('connect_error') && <div class="hint-box" style="margin:14px 16px 0" role="alert">{t('order.connectFailed', { app: name })}</div>}

      {p && !p.connected && (
        <section class="card" style="margin:16px;padding:18px;display:flex;flex-direction:column;gap:12px">
          <div class="display" style="font-weight:700;font-size:19px">{t('order.connectTitle', { app: name })}</div>
          <div class="muted" style="line-height:1.6">{t('food.connectBody', { app: name })}</div>
          <button class="btn btn-primary" disabled={busy} onClick={() => { setBusy(true); startConnect(provider, location.pathname + location.search).catch((e: ApiError) => { setBusy(false); showToast(e.message); }); }}>
            {t('order.connect', { app: name })}
          </button>
        </section>
      )}

      {err && <ErrorBox message={err} />}

      {p?.connected && step === 'address' && (
        <section style="padding:16px;display:flex;flex-direction:column;gap:10px">
          <h2 class="section-label" style="margin:0 4px">{t('food.deliverTo')}</h2>
          {addresses === null && !err && <div class="skeleton" style="height:120px" aria-label={t('common.loading')} />}
          {addresses?.length === 0 && <div class="hint-box">{t('food.noAddress', { app: name })}</div>}
          {addresses && addresses.length > 0 && (
            <div class="rows">
              {addresses.map((a) => (
                <button class="row" onClick={() => { setAddress(a); save(addrKey, a.id); setStep('search'); }}>
                  <span class="grow"><span style="font-weight:700">{a.label || t('food.address')}</span><span class="sub">{a.line}</span></span>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {p?.connected && step === 'search' && address && (
        <>
          <button class="row" style="margin:12px 16px 0;width:calc(100% - 32px);border:1px solid var(--line);border-radius:14px" onClick={() => setStep('address')}>
            <span class="grow"><span class="sub">{t('food.deliverTo')}</span><span style="font-weight:600">{address.label ? `${address.label} · ` : ''}{address.line}</span></span>
            <span class="small" style="color:var(--accent-text);font-weight:700">{t('food.change')}</span>
          </button>
          <form class="pad" style="padding-top:12px;display:flex;gap:8px" onSubmit={(e) => { e.preventDefault(); setDishes(null); setDish(null); search(); }}>
            <label class="field" style="flex:1"><SearchIcon /><input value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} aria-label={t('food.dish')} placeholder={t('food.dish')} /></label>
            <button class="chip" type="button" aria-pressed={veg} style="height:52px" onClick={() => { setVeg(!veg); setDishes(null); }}><VegMark veg={true} size={13} />{t('common.veg')}</button>
          </form>
          {veg && dishes === null && !busy && <div class="pad" style="padding-top:8px"><button class="btn btn-outline" onClick={search}>{t('food.search')}</button></div>}
          {busy && !dishes && <div class="pad list" style="padding-top:12px">{[1, 2, 3].map(() => <div class="skeleton" style="height:76px" />)}</div>}
          {dishes?.length === 0 && <div class="hint-box" style="margin:14px 16px 0">{t('food.none', { app: name })}</div>}
          {dishes && dishes.length > 0 && (
            <>
              <div class="section-label" style="padding:16px 20px 4px">{t('food.dishesNear', { n: dishes.length })}</div>
              <div class="pad list" style="padding-top:8px">
                {dishes.map((d) => {
                  const orderable = d.in_stock && !d.customizable;
                  const picked = dish?.item_id === d.item_id && dish.restaurant_id === d.restaurant_id;
                  return (
                    <article class="dish-card" style={`${picked ? 'border:2px solid var(--ink);' : ''}${orderable ? '' : 'opacity:.55'}`}>
                      <span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:4px">
                        <VegMark veg={d.is_veg} unknown />
                        <span class="display clamp3" style="font-weight:700;font-size:18px;line-height:1.3">{d.name}</span>
                        <span style="font-weight:800;font-size:16px">{money(d.price)}</span>
                        <span class="small muted clamp2" style="line-height:1.4">{d.restaurant_name}</span>
                        {d.rating && <span class="pill" style="align-self:flex-start;margin-top:2px">★ {d.rating}</span>}
                      </span>
                      <span style="flex:none;width:112px;display:flex;flex-direction:column;align-items:center">
                        <Thumb src={d.image_url} name={d.name} size="dish" />
                        {picked ? (
                          <span class="dish-add on" role="group" aria-label={d.name}>
                            <button aria-label={t('order.less')} style="width:32px;height:36px;border:0;background:transparent;font:inherit" onClick={() => (qty <= 1 ? setDish(null) : setQty(qty - 1))}>−</button>
                            <span aria-live="polite" style="min-width:18px;text-align:center">{qty}</span>
                            <button aria-label={t('order.more')} style="width:32px;height:36px;border:0;background:transparent;font:inherit" disabled={qty >= 10} onClick={() => setQty(qty + 1)}>+</button>
                          </span>
                        ) : (
                          <button class="dish-add" disabled={!orderable} aria-label={`${t('food.add')} ${d.name}`}
                            style={orderable ? '' : 'border-color:var(--night-muted);color:var(--muted)'} onClick={() => { setDish(d); setQty(1); }}>
                            {d.in_stock ? t('food.add') : t('food.soldOutShort')}
                          </button>
                        )}
                        {d.in_stock && d.customizable && <span class="small muted" style="margin-top:4px;text-align:center;line-height:1.3">{t('food.optionsInApp', { app: name })}</span>}
                      </span>
                    </article>
                  );
                })}
              </div>
            </>
          )}
          {dish && (
            <div class="bottom-bar">
              <div class="bottom-bar-inner">
                <span style="flex:1;min-width:0;display:flex;flex-direction:column;line-height:1.25">
                  <span style="font-weight:800;font-size:18px">{t('food.oneItem', { n: qty, total: money({ amount: dish.price.amount * qty, currency: dish.price.currency }) })}</span>
                  <span class="small muted" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{dish.restaurant_name}</span>
                </span>
                <button class="btn btn-primary" style="min-height:54px;border-radius:16px;padding:0 22px" disabled={busy} onClick={review}>{busy ? t('common.loading') : t('food.review')}</button>
              </div>
            </div>
          )}
        </>
      )}

      {step === 'review' && cart && address && (
        <section style="padding:16px 16px calc(190px + env(safe-area-inset-bottom));display:flex;flex-direction:column;gap:12px">
          <div class="display" style="font-weight:700;font-size:18px;padding:0 4px">{cart.restaurant_name}</div>
          <CartSummary totals={cart.totals} lines={cart.lines.map((l) => ({
            key: l.item_id, title: l.name, qty: l.quantity, price: l.price,
            image: dish && l.item_id === dish.item_id ? dish.image_url : undefined,
          }))} />
          <AddressCard label={address.label} line={address.line} />
          <div style="display:flex;flex-direction:column;gap:8px">
            <h2 class="section-label" style="margin:0 4px">{t('food.payWith')}</h2>
            {cart.payments.length === 0 && <div class="hint-box">{t('food.noPayment', { app: name })}</div>}
            <div class="rows" role="radiogroup" aria-label={t('food.payWith')}>
              {cart.payments.map((pm) => (
                <button class="row" role="radio" aria-checked={method === pm.method} onClick={() => setMethod(pm.method)} style={method === pm.method ? 'background:var(--soft)' : ''}>
                  <span class="checkbox" style={`border-radius:999px;${method === pm.method ? 'background:var(--ink)' : ''}`} />
                  <span class="grow" style="font-weight:600">{pm.label}</span>
                </button>
              ))}
            </div>
          </div>
          <label style="display:flex;flex-direction:column;gap:6px">
            <span class="small muted">{t('food.note')}</span>
            <input class="input" maxLength={120} value={note} placeholder={t('food.notePlaceholder')} onInput={(e) => setNote((e.target as HTMLInputElement).value)} />
          </label>
          {cart.messages?.map((m) => <div class="hint-box">{m}</div>)}
          <div class="small muted" style="line-height:1.5">{t('food.cancelNote', { app: name })}</div>
          <ReviewActions appURL={p?.app_cart_url} appName={name} place={
            <button class="btn btn-primary btn-lg" disabled={busy || !method} onClick={place}>
              {busy ? t('food.placing') : method ? t('food.place', { total: money(cart.total), method: methodLabel }) : t('food.choosePayment')}
            </button>
          } />
        </section>
      )}

      {step === 'done' && order && (
        <section style="padding:16px;display:flex;flex-direction:column;gap:12px" role="status" aria-live="polite">
          {order.status === 'confirmed' && (
            <div class="card" style="padding:18px;display:flex;flex-direction:column;gap:8px">
              <div class="display" style="font-weight:800;font-size:22px">{t('food.placed', { app: name })}</div>
              <div class="muted">{[order.id && t('food.orderId', { id: order.id }), (track?.eta || order.eta) && t('food.eta', { eta: track?.eta || order.eta || '' })].filter(Boolean).join(' · ')}</div>
              {track?.message && <div>{track.message}</div>}
              <button class="btn btn-outline" onClick={refreshTrack}>{t('food.refresh')}</button>
            </div>
          )}
          {order.status === 'pending_payment' && (
            <div class="card" style="padding:18px;display:flex;flex-direction:column;gap:10px">
              <div class="display" style="font-weight:800;font-size:20px">{t('food.payNow')}</div>
              <div class="muted">{t('food.payBody', { app: name })}</div>
              {order.pay_url && <a class="btn btn-primary" href={order.pay_url}>{t('food.openUPI')}</a>}
              <div class="small muted">{t('food.waiting')}</div>
            </div>
          )}
          {order.status === 'failed' && (
            <div class="card" style="padding:18px;display:flex;flex-direction:column;gap:10px" role="alert">
              <div class="display" style="font-weight:800;font-size:20px">{t('food.failed')}</div>
              {order.message && <div class="muted">{order.message}</div>}
              <button class="btn btn-outline" onClick={() => { placing.current = false; setOrder(null); setStep('review'); }}>{t('common.tryAgain')}</button>
            </div>
          )}
          <Link href="/" class="btn-text" style="text-align:center">{t('nav.home')}</Link>
        </section>
      )}
      {toast}
    </div>
  );
}
